import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowLeft, Activity, Flame, Heart, Send, Save, Dumbbell, Bell } from "lucide-react"
import { toast } from "sonner"
import {
  adminGetUserDetail,
  adminUpdateCoachNotes,
  adminSendPush,
  adminRevokeInjuryConsent,
  type AdminUserDetail,
  type TrainingLoadRow,
  type SubscriptionStatus,
} from "@/lib/supabase"
import {
  computeACWR,
  computeStrain,
  computeStrainByCalendarWeek,
  getHooperZone,
} from "@/lib/monitoring/calculations"
import { startOfWeek } from "@/lib/utils"
import { useCurrentBodyweight } from "@/hooks/useCurrentBodyweight"
import {
  getGoalLabel,
  getLevelLabel,
  getPlanLabel,
  getPlaceLabel,
  getGenderLabel,
  formatDateFR,
  getInjuryZoneLabel,
  INJURY_STATUS_LABELS,
} from "@/data/labels"

const SUB_COLOR: Record<string, string> = {
  active: "#22c55e",
  trialing: "#06b6d4",
  past_due: "#f97316",
  canceled: "#94a3b8",
  incomplete: "#f97316",
}

const SUB_LABEL: Record<string, string> = {
  active: "Actif",
  trialing: "Essai",
  past_due: "Échec paiement",
  canceled: "Résilié",
  incomplete: "Incomplet",
}

function formatDateShort(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })
  } catch {
    return iso
  }
}

function formatDateTimeFR(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

// 4 semaines CALENDAIRES de strain (lundi→dimanche), la dernière étant celle en cours.
//
// Reprend `computeStrainByCalendarWeek` de la lib au lieu de la copie locale qui
// existait ici. Cette copie découpait en fenêtres glissantes de 7 jours ancrées à
// aujourd'hui alors que la tuile Strain juste au-dessus est désormais calendaire :
// la garder aurait recréé côté coach l'incohérence exacte que ce chantier corrige
// côté athlète. Elle datait aussi ses jours en `toISOString()` (UTC), là où le reste
// de l'app utilise `toLocalISODate` — un décalage d'un jour entre minuit et 2h.
const ADMIN_STRAIN_WEEKS_BACK = 3

function adminStrainWeeks(loads: TrainingLoadRow[]): { weekLabel: string; strain: number | null }[] {
  const start = startOfWeek()
  start.setDate(start.getDate() - ADMIN_STRAIN_WEEKS_BACK * 7)
  return computeStrainByCalendarWeek(loads, start, ADMIN_STRAIN_WEEKS_BACK).map((w, i) => ({
    weekLabel: `S-${ADMIN_STRAIN_WEEKS_BACK - i}`,
    strain: w.strain,
  }))
}

export function AdminUserDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  // Poids actuel de l'utilisateur consulté : dernière pesée, repli sur l'inscription
  // (la base autorise l'admin à lire `weight_history`).
  const { bodyweightKg, loading: bodyweightLoading } = useCurrentBodyweight(id)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [coachNotes, setCoachNotes] = useState("")
  const [savingNotes, setSavingNotes] = useState(false)

  const [pushMessage, setPushMessage] = useState("")
  const [sendingPush, setSendingPush] = useState(false)

  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [revoking, setRevoking] = useState(false)

  useEffect(() => {
    if (!id) return
    let mounted = true
    setLoading(true)
    setError(null)
    adminGetUserDetail(id)
      .then((d) => {
        if (!mounted) return
        setDetail(d)
        setCoachNotes(d.athlete_profile?.coach_notes ?? "")
      })
      .catch((err: Error) => {
        if (mounted) setError(err.message)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [id])

  const acwr = useMemo(() => detail ? computeACWR(detail.training_load_28d) : null, [detail])
  const strain = useMemo(() => detail ? computeStrain(detail.training_load_28d) : null, [detail])
  const weeklyStrains = useMemo(() => detail ? adminStrainWeeks(detail.training_load_28d) : [], [detail])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#060a13" }}>
        <div className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: "#060a13" }}>
        <p className="text-[14px] text-red-400 mb-4">{error ?? "Utilisateur introuvable"}</p>
        <button
          onClick={() => navigate("/admin/users")}
          className="text-[12px] text-cyan-400 underline cursor-pointer"
        >
          Retour à la liste
        </button>
      </div>
    )
  }

  const w = detail.wizard
  const sub = detail.subscription
  const subStatus: SubscriptionStatus | null = sub?.status ?? null
  const subColor = subStatus ? SUB_COLOR[subStatus] ?? "#94a3b8" : "#94a3b8"
  const subLabel = subStatus ? SUB_LABEL[subStatus] ?? subStatus : "Pas d'abo"

  const handleSaveNotes = async () => {
    if (!id) return
    setSavingNotes(true)
    try {
      await adminUpdateCoachNotes(id, coachNotes)
      toast.success("Notes coach sauvegardées")
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue"
      toast.error(`Sauvegarde échouée : ${msg}`)
    } finally {
      setSavingNotes(false)
    }
  }

  // Client qui demande par email le retrait de son accord blessures : même fonction
  // transactionnelle que le bouton client (blessures + texte libre + preuve d'accord).
  const handleRevokeInjuryConsent = async () => {
    if (!id || revoking) return
    setRevoking(true)
    try {
      await adminRevokeInjuryConsent(id)
      setDetail((d) => d ? {
        ...d,
        injuries: [],
        athlete_profile: d.athlete_profile ? { ...d.athlete_profile, injuries: null } : null,
        wizard: { ...d.wizard, injury_consent_at: null, injury_consent_version: null },
      } : d)
      setConfirmRevoke(false)
      toast.success("Accord retiré, blessures effacées")
    } catch (err) {
      toast.error("Retrait impossible", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setRevoking(false)
    }
  }

  const handleSendPush = async () => {
    if (!id || !pushMessage.trim()) return
    setSendingPush(true)
    try {
      const result = await adminSendPush(id, pushMessage.trim())
      if (result.success) {
        const plural = result.sent > 1 ? "s" : ""
        toast.success(`Push envoyé à ${result.sent} appareil${plural}`)
        setPushMessage("")
      } else if (result.reason === "no_subscription") {
        toast.error("Aucun appareil n'est enregistré pour ce user.")
      } else {
        toast.error("Échec d'envoi du push (tous les endpoints ont échoué).")
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue"
      toast.error(`Push échoué : ${msg}`)
    } finally {
      setSendingPush(false)
    }
  }

  return (
    <div className="min-h-screen pb-12" style={{ background: "#060a13" }}>
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-5 pb-3 sticky top-0 z-20"
        style={{
          background: "rgba(6,10,19,0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(30,41,59,0.4)",
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/users")}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-800/60 transition-colors cursor-pointer"
            style={{ background: "rgba(30,41,59,0.4)" }}
            aria-label="Retour à la liste"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-[18px] font-bold text-white tracking-tight truncate">{w.first_name}</h1>
            <p className="text-[11px] text-gray-500 truncate">{w.email}</p>
          </div>
          <span
            className="text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap"
            style={{ background: `${subColor}20`, color: subColor }}
          >
            {subLabel}
          </span>
        </div>
      </motion.div>

      <div className="px-4 pt-4 space-y-4">
        {/* PROFIL */}
        <Section title="Profil">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
            <ProfileRow label="Genre" value={getGenderLabel(w.gender)} />
            <ProfileRow label="Âge" value={`${w.age} ans`} />
            <ProfileRow label="Taille" value={`${w.height} cm`} />
            <ProfileRow label="Poids" value={bodyweightLoading ? "…" : `${bodyweightKg ?? w.weight} kg`} />
            <ProfileRow label="Lieu" value={getPlaceLabel(w.place)} />
            <ProfileRow label="Niveau" value={getLevelLabel(w.level_id)} />
            <ProfileRow label="Objectif" value={getGoalLabel(w.goal_id)} />
            {/* L'« offre » du wizard (offer_id) n'est plus écrite depuis le lot 3 : la formule
                est celle de l'abonnement. */}
            <ProfileRow label="Formule" value={getPlanLabel(sub?.plan)} />
            <ProfileRow label="Inscrit le" value={formatDateFR(w.created_at)} />
            {sub?.trial_ends_at && (
              <ProfileRow label="Essai jusqu'au" value={formatDateFR(sub.trial_ends_at)} />
            )}
          </div>
        </Section>

        {/* BLESSURES DÉCLARÉES (diagnostic, lot 3) — données de santé, sous accord */}
        {(detail.injuries.length > 0 || w.injury_consent_at) && (
          <Section title="Blessures déclarées">
            {detail.injuries.length === 0 ? (
              <p className="text-[12px] text-gray-500">Accord donné, aucune zone déclarée.</p>
            ) : (
              <div className="space-y-2 text-[12px]">
                {detail.injuries.map((i) => (
                  <div key={i.id} className="flex items-baseline justify-between gap-3">
                    <span className="text-white font-medium">{getInjuryZoneLabel(i.zone, i.zone_other)}</span>
                    <span style={{ color: i.status === "genante" ? "#fbbf24" : "#9ca3af" }}>{INJURY_STATUS_LABELS[i.status]}</span>
                  </div>
                ))}
              </div>
            )}
            {w.injury_consent_at && (
              <p className="mt-3 text-[10.5px] text-gray-500">
                Accord du {formatDateFR(w.injury_consent_at)} · texte {w.injury_consent_version ?? "—"}
              </p>
            )}
            {confirmRevoke ? (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setConfirmRevoke(false)}
                  disabled={revoking}
                  className="flex-1 py-2 rounded-lg text-[12px] font-semibold text-gray-300 cursor-pointer"
                  style={{ background: "rgba(30,41,59,0.5)" }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleRevokeInjuryConsent}
                  disabled={revoking}
                  className="flex-1 py-2 rounded-lg text-[12px] font-bold text-white cursor-pointer disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg,#dc2626,#ef4444)" }}
                >
                  Tout effacer
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRevoke(true)}
                className="mt-3 w-full py-2 rounded-lg text-[12px] font-semibold cursor-pointer"
                style={{ border: "1px solid rgba(248,113,113,0.35)", color: "#f87171" }}
              >
                Retirer l'accord (demande du client)
              </button>
            )}
          </Section>
        )}

        {/* ATHLETE PROFILE (1RM + blessures) */}
        {detail.athlete_profile && (
          <Section title="1RM & blessures" icon={<Dumbbell className="w-3.5 h-3.5" />}>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
              <ProfileRow label="Bench" value={detail.athlete_profile.rm_bench_press ? `${detail.athlete_profile.rm_bench_press} kg` : "—"} />
              <ProfileRow label="Squat" value={detail.athlete_profile.rm_back_squat ? `${detail.athlete_profile.rm_back_squat} kg` : "—"} />
              <ProfileRow label="Deadlift" value={detail.athlete_profile.rm_deadlift ? `${detail.athlete_profile.rm_deadlift} kg` : "—"} />
              <ProfileRow label="Pull-up" value={detail.athlete_profile.rm_pull_up ? `${detail.athlete_profile.rm_pull_up} kg` : "—"} />
              <ProfileRow label="Overhead" value={detail.athlete_profile.rm_overhead_press ? `${detail.athlete_profile.rm_overhead_press} kg` : "—"} />
              <ProfileRow label="Test 1RM" value={formatDateFR(detail.athlete_profile.rm_test_date)} />
            </div>
            {detail.athlete_profile.injuries && (
              <div className="mt-3 text-[12px]">
                <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Blessures</p>
                <p className="text-gray-300 leading-relaxed">{detail.athlete_profile.injuries}</p>
              </div>
            )}
            {detail.athlete_profile.medical_notes && (
              <div className="mt-3 text-[12px]">
                <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Notes médicales</p>
                <p className="text-gray-300 leading-relaxed">{detail.athlete_profile.medical_notes}</p>
              </div>
            )}
          </Section>
        )}

        {/* MONITORING (28J) */}
        <Section title="Monitoring · 28 jours">
          {/* ACWR */}
          {acwr && (
            <KpiBlock
              icon={<Activity className="w-4 h-4" />}
              label="ACWR"
              value={acwr.acwr.toFixed(2)}
              statusLabel={acwr.label}
              color={acwr.color}
              subline={`Aigu ${acwr.acuteEWMA} · Chronique ${acwr.chronicEWMA} UA/j`}
            >
              {detail.training_load_28d.length === 0 ? (
                <ChartEmpty />
              ) : (
                <SimpleChart
                  data={detail.training_load_28d.map((l) => ({ date: l.date, value: l.training_load }))}
                  yMin={0}
                  yMax={Math.max(500, ...detail.training_load_28d.map((l) => l.training_load))}
                  color={acwr.color}
                />
              )}
            </KpiBlock>
          )}

          {/* STRAIN */}
          {strain && (
            <KpiBlock
              icon={<Flame className="w-4 h-4" />}
              label="Strain (semaine)"
              value={strain.hasData ? strain.strain.toString() : "—"}
              statusLabel={strain.label}
              color={strain.color}
              subline={
                strain.hasData
                  ? `Charge sem. ${strain.weeklyLoad} UA · Monotonie ${strain.monotony}`
                  : "Semaine en cours : moins de 2 jours d'entraînement"
              }
            >
              {(() => {
                // Les semaines sans verdict sont retirées plutôt que rendues à 0 :
                // SimpleChart ne sait pas dessiner de trou, et un 0 ferait plonger la
                // courbe comme si la charge s'était effondrée.
                const measured = weeklyStrains.filter(
                  (w): w is { weekLabel: string; strain: number } => w.strain !== null
                )
                return measured.length === 0 ? (
                  <ChartEmpty />
                ) : (
                  <SimpleChart
                    data={measured.map((w) => ({ date: w.weekLabel, value: w.strain }))}
                    yMin={0}
                    yMax={Math.max(2000, ...measured.map((w) => w.strain))}
                    color={strain.color}
                  />
                )
              })()}
            </KpiBlock>
          )}

          {/* HOOPER */}
          {(() => {
            const lastHooper = detail.daily_wellness_28d.length > 0
              ? detail.daily_wellness_28d[detail.daily_wellness_28d.length - 1]
              : null
            const zone = lastHooper ? getHooperZone(lastHooper.hooper_score) : null
            return (
              <KpiBlock
                icon={<Heart className="w-4 h-4" />}
                label="Hooper"
                value={lastHooper ? `${lastHooper.hooper_score}/28` : "—"}
                statusLabel={zone?.label ?? "Pas de données"}
                color={zone?.color ?? "#94a3b8"}
                subline={lastHooper ? `Dernier rempli ${formatDateShort(lastHooper.date)}` : ""}
              >
                {detail.daily_wellness_28d.length === 0 ? (
                  <ChartEmpty />
                ) : (
                  <SimpleChart
                    data={detail.daily_wellness_28d.map((d) => ({ date: d.date, value: d.hooper_score }))}
                    yMin={4}
                    yMax={28}
                    color={zone?.color ?? "#06b6d4"}
                  />
                )}
              </KpiBlock>
            )
          })()}
        </Section>

        {/* NOTES COACH */}
        <Section title="Notes coach">
          <textarea
            value={coachNotes}
            onChange={(e) => setCoachNotes(e.target.value)}
            placeholder="Observations sur l'athlète, points d'attention, plan de suivi…"
            rows={5}
            className="w-full text-[13px] rounded-lg p-3 resize-y outline-none focus:ring-1 focus:ring-cyan-500/40"
            style={{
              background: "rgba(6,10,19,0.6)",
              border: "1px solid rgba(30,41,59,0.55)",
              color: "#e5e7eb",
            }}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className="text-[12px] font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #0891b2, #06b6d4)", color: "#fff" }}
            >
              <Save className="w-3.5 h-3.5" />
              {savingNotes ? "Sauvegarde…" : "Sauvegarder"}
            </button>
          </div>
        </Section>

        {/* ÉTAT DES NOTIFICATIONS PUSH
            Un abonnement coupé par le service push (410 / 404) était supprimé sans
            trace : le client ne recevait plus rien, et rien ne le signalait ici. */}
        <Section title="Notifications push" icon={<Bell className="w-3.5 h-3.5" />}>
          {detail.push_devices.length > 0 ? (
            <>
              <p className="text-[13px] text-white">
                Actives sur {detail.push_devices.length} appareil{detail.push_devices.length > 1 ? "s" : ""}
              </p>
              <ul className="mt-1.5 space-y-1">
                {detail.push_devices.map((d) => (
                  <li key={`${d.push_service}-${d.created_at}`} className="text-[12px] text-gray-400">
                    {describePushDevice(d.push_service, d.user_agent)} · depuis le {formatDateFR(d.created_at)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-[13px] text-amber-400">
              {detail.push_events.length > 0
                ? `Coupées le ${formatDateFR(detail.push_events[0].occurred_at)} (abonnement expiré · ${describePushDevice(detail.push_events[0].push_service, detail.push_events[0].user_agent)})`
                : "Aucun appareil abonné"}
            </p>
          )}
          {detail.push_events.length > 0 && detail.push_devices.length > 0 && (
            <p className="text-[11px] text-gray-500 mt-2">
              Dernière coupure : {formatDateFR(detail.push_events[0].occurred_at)} ·{" "}
              {describePushDevice(detail.push_events[0].push_service, detail.push_events[0].user_agent)}
            </p>
          )}
        </Section>

        {/* PUSH ADMIN */}
        <Section title="Envoyer un push">
          <textarea
            value={pushMessage}
            onChange={(e) => setPushMessage(e.target.value)}
            placeholder="Message court à envoyer en notification push (200 caractères max)…"
            rows={3}
            maxLength={200}
            className="w-full text-[13px] rounded-lg p-3 resize-y outline-none focus:ring-1 focus:ring-cyan-500/40"
            style={{
              background: "rgba(6,10,19,0.6)",
              border: "1px solid rgba(30,41,59,0.55)",
              color: "#e5e7eb",
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-gray-500">{pushMessage.length} / 200</span>
            <button
              onClick={handleSendPush}
              disabled={sendingPush || !pushMessage.trim()}
              className="text-[12px] font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #0891b2, #06b6d4)", color: "#fff" }}
            >
              <Send className="w-3.5 h-3.5" />
              {sendingPush ? "Envoi…" : "Envoyer"}
            </button>
          </div>
        </Section>

        {/* HISTORIQUE SÉANCES */}
        <Section title={`Historique séances · 28 jours (${detail.session_feedback_28d.length})`}>
          {detail.session_feedback_28d.length === 0 ? (
            <p className="text-[12px] text-gray-500 text-center py-4">Aucune séance complétée sur la période.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {[...detail.session_feedback_28d].reverse().map((s) => (
                <li
                  key={s.id}
                  className="rounded-lg p-3 text-[12px]"
                  style={{ background: "rgba(6,10,19,0.6)", border: "1px solid rgba(30,41,59,0.4)" }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-white">{formatDateTimeFR(s.completed_at)}</span>
                    <span className="text-gray-500">{s.actual_duration_minutes} min</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400">
                    <span>RPE <strong className="text-white">{s.rpe}</strong></span>
                    <span>Plaisir <strong className="text-white">{s.enjoyment}</strong></span>
                    {s.fatigue !== null && <span>Fatigue <strong className="text-white">{s.fatigue}</strong></span>}
                  </div>
                  {s.notes && (
                    <p className="text-gray-400 mt-1.5 italic">"{s.notes}"</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  )
}

// ============================================
// SUB-COMPONENTS
// ============================================

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4"
      style={{ background: "rgba(12,20,35,0.6)", border: "1px solid rgba(30,41,59,0.55)" }}
    >
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-cyan-400">{icon}</span>}
        <h2 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{title}</h2>
      </div>
      {children}
    </motion.section>
  )
}

/**
 * « iPhone », « Chrome · Windows »… à partir du service push (hôte de l'adresse
 * d'envoi) et du navigateur enregistré à l'abonnement.
 */
function describePushDevice(service: string, userAgent: string | null): string {
  const ua = userAgent ?? ""
  const os = /iPhone|iPad/.test(ua) ? "iPhone"
    : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows"
    : /Mac OS X/.test(ua) ? "Mac"
    : null
  const browser = service.endsWith("push.apple.com") ? "Safari"
    : service === "fcm.googleapis.com" ? (/Edg\//.test(ua) ? "Edge" : "Chrome")
    : service.includes("mozilla") ? "Firefox"
    : service
  if (os === "iPhone" && browser === "Safari") return "iPhone"
  return os ? `${browser} · ${os}` : browser
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-white">{value}</p>
    </div>
  )
}

function KpiBlock({
  icon,
  label,
  value,
  statusLabel,
  color,
  subline,
  children,
}: {
  icon: React.ReactNode
  label: string
  value: string
  statusLabel: string
  color: string
  subline: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl p-3 mb-3 last:mb-0" style={{ background: "rgba(6,10,19,0.5)", border: "1px solid rgba(30,41,59,0.4)" }}>
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30`, color }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-white">{label}</p>
          <p className="text-[10px]" style={{ color }}>{statusLabel}</p>
        </div>
        <span className="text-[18px] font-extrabold" style={{ color }}>{value}</span>
      </div>
      {subline && <p className="text-[10px] text-gray-500 mb-2">{subline}</p>}
      {children}
    </div>
  )
}

function ChartEmpty() {
  return (
    <p className="text-[11px] text-gray-500 text-center py-4">Pas de données.</p>
  )
}

function SimpleChart({
  data,
  yMin,
  yMax,
  color,
}: {
  data: { date: string; value: number }[]
  yMin: number
  yMax: number
  color: string
}) {
  if (data.length === 0) return <ChartEmpty />

  const width = 280
  const height = 70
  const padding = 6
  const range = yMax - yMin || 1

  const points = data.map((d, i) => {
    const x = padding + (i / Math.max(1, data.length - 1)) * (width - padding * 2)
    const y = height - padding - ((d.value - yMin) / range) * (height - padding * 2)
    return { x, y, value: d.value, date: d.date }
  })

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ")
  const areaPath = `${path} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`
  const gradId = `grad-${color.replace("#", "")}-${Math.floor(Math.random() * 100000)}`

  return (
    <div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="1.8" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="1.8" fill={color} />
        ))}
      </svg>
      <div className="flex justify-between text-[9px] text-gray-600 mt-1">
        <span>{data[0].date.length === 10 ? formatDateShort(data[0].date) : data[0].date}</span>
        {data.length > 2 && (
          <span>
            {data[Math.floor(data.length / 2)].date.length === 10
              ? formatDateShort(data[Math.floor(data.length / 2)].date)
              : data[Math.floor(data.length / 2)].date}
          </span>
        )}
        <span>{data[data.length - 1].date.length === 10 ? formatDateShort(data[data.length - 1].date) : data[data.length - 1].date}</span>
      </div>
    </div>
  )
}
